/** A workspace bot's face: a white robot head on a disc of its accent colour,
 *  drawn once for the web (through react-native-web) and the native app. */
import React from 'react';
import Svg, { Circle, Ellipse, Line, Rect, Path } from 'react-native-svg';
import { BOT_ACCENT_HEX, type BotAppearance, type BotAvatarStyle } from '../../features/workspaces/bots/botAppearance';

const INK = 'rgba(15, 23, 42, 0.82)';

/** Head and face per style, drawn in a 40×40 box. `accent` tints details. */
function Face({ style, accent }: { style: BotAvatarStyle; accent: string }) {
  switch (style) {
    case 'round':
      return <>
        <Circle cx={20} cy={21} r={11} fill="#fff" />
        <Circle cx={16} cy={20} r={2} fill={INK} /><Circle cx={24} cy={20} r={2} fill={INK} />
        <Path d="M16 25 Q20 28 24 25" stroke={accent} strokeWidth={1.8} fill="none" />
      </>;
    case 'visor':
      return <>
        <Rect x={9} y={12} width={22} height={18} rx={7} fill="#fff" />
        <Rect x={12} y={17} width={16} height={5.5} rx={2.75} fill={INK} />
        <Rect x={14} y={18.6} width={5} height={2.3} rx={1.15} fill={accent} />
        <Rect x={17} y={25.5} width={6} height={1.6} rx={0.8} fill={INK} opacity={0.5} />
      </>;
    case 'antenna':
      return <>
        <Line x1={20} y1={8.5} x2={20} y2={13} stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
        <Circle cx={20} cy={7.5} r={2.2} fill="#fff" />
        <Rect x={8} y={18} width={2.6} height={6} rx={1.3} fill="#fff" /><Rect x={29.4} y={18} width={2.6} height={6} rx={1.3} fill="#fff" />
        <Rect x={11} y={13} width={18} height={17} rx={5} fill="#fff" />
        <Circle cx={16.5} cy={20} r={2.1} fill={INK} /><Circle cx={23.5} cy={20} r={2.1} fill={INK} />
        <Rect x={16} y={25} width={8} height={1.8} rx={0.9} fill={accent} />
      </>;
    case 'screen':
      return <>
        <Rect x={9} y={11} width={22} height={19} rx={4} fill="#fff" />
        <Rect x={11.5} y={13.5} width={17} height={11} rx={2.5} fill={INK} />
        <Circle cx={16.5} cy={19} r={1.6} fill={accent} /><Circle cx={23.5} cy={19} r={1.6} fill={accent} />
        <Rect x={15} y={31} width={10} height={2} rx={1} fill="#fff" />
      </>;
    case 'pod':
      return <>
        <Rect x={12} y={9} width={16} height={23} rx={8} fill="#fff" />
        <Circle cx={20} cy={18} r={4} fill={INK} /><Circle cx={20} cy={18} r={1.7} fill={accent} />
        <Rect x={17} y={25} width={6} height={1.8} rx={0.9} fill={INK} opacity={0.5} />
      </>;
    case 'classic':
    default:
      return <>
        <Line x1={20} y1={9} x2={20} y2={12} stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
        <Rect x={10} y={12} width={20} height={17} rx={4.5} fill="#fff" />
        <Rect x={13.5} y={17} width={4.5} height={4.5} rx={1.2} fill={INK} /><Rect x={22} y={17} width={4.5} height={4.5} rx={1.2} fill={INK} />
        <Rect x={15} y={24.5} width={10} height={1.8} rx={0.9} fill={accent} />
      </>;
  }
}

export function BotFace({ appearance, size, label }: {
  appearance: BotAppearance;
  size: number;
  /** Accessible name; omit when the bot's name is already next to it. */
  label?: string;
}) {
  const accent = BOT_ACCENT_HEX[appearance.color];
  return (
    <Svg width={size} height={size} viewBox="0 0 40 40" accessibilityLabel={label}>
      {/* A highlight, not a gradient: url(#id) references break when the
          same markup is rendered twice on a page, as in a picker. */}
      <Circle cx={20} cy={20} r={20} fill={accent} />
      <Ellipse cx={20} cy={9} rx={17} ry={11} fill="#fff" opacity={0.14} />
      <Face style={appearance.style} accent={accent} />
    </Svg>
  );
}
